import { message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useEffect, useRef, useState } from 'react';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import orderApi from '../../apis/order';
import { ProductTypeOptions } from '../../constants/order';
import { ISize, ProductType } from '../../interfaces/Order';
import { PaginationParamsResponse } from '../../interfaces/PaginationParamsResponse';
import { FlexContainer, PageContainer } from '../../styles/Global';
import Button from '../Button/Button';
import { StyledSelect } from '../Order/Order.styled';
import PageHeader from '../PageHeader/PageHeader';
import Table from '../Table/Table';

const PER_PAGE = 20;

// Use the antd Table directly
// const StyledTable = styled(Table)`
//   .drop-over-downward td {
//     border-bottom: 2px dashed #1890ff !important;
//   }

//   .drop-over-upward td {
//     border-top: 2px dashed #1890ff !important;
//   }

//   .ant-table-row.dragging {
//     opacity: 0.5;
//   }

//   /* Reduce row height */
//   .ant-table-tbody > tr > td {
//     padding-top: 8px !important;
//     padding-bottom: 8px !important;
//   }

//   .ant-table-thead > tr > th {
//     padding-top: 8px !important;
//     padding-bottom: 8px !important;
//   }
// `;

// Draggable body row component
const DraggableBodyRow = (props: any) => {
  const { record, index, moveRow, className, style, canDrag, ...restProps } =
    props;
  const ref = useRef(null);

  const [{ isOver, dropClassName }, drop] = useDrop({
    accept: 'row',
    canDrop: () => canDrag,
    collect: (monitor) => {
      const { index: dragIndex } = monitor.getItem() || {};
      if (dragIndex === index) {
        return {};
      }
      return {
        isOver: canDrag ? monitor.isOver() : false,
        dropClassName:
          dragIndex < index ? 'drop-over-downward' : 'drop-over-upward',
      };
    },
    drop: (item: any) => {
      if (canDrag) {
        moveRow(item.index, index);
      }
    },
  });

  const [{ isDragging }, drag] = useDrag({
    type: 'row',
    item: { index },
    canDrag: () => canDrag,
    collect: (monitor) => ({
      isDragging: canDrag ? monitor.isDragging() : false,
    }),
  });

  drop(drag(ref));

  return (
    <tr
      ref={ref}
      className={`${className} ${isOver ? dropClassName : ''} ${
        isDragging ? 'dragging' : ''
      }`}
      style={{ cursor: canDrag ? 'move' : 'default', ...style }}
      {...restProps}
    />
  );
};

const SizeList = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [itemsPagination, setItemsPagintion] = useState<
    PaginationParamsResponse<ISize>
  >({
    items: [],
    page: 1,
    perPage: PER_PAGE,
    total: 0,
    pageCount: 0,
  });
  const productParam = new URLSearchParams(location.search).get('product');
  // Treat both missing and empty-string ("View All") as "no filter".
  const product: ProductType | undefined = productParam
    ? (productParam as ProductType)
    : undefined;
  const [canReorder, setCanReorder] = useState(false);

  const moveRow = (dragIndex: number, hoverIndex: number) => {
    if (dragIndex === hoverIndex) return;

    const newData = [...itemsPagination.items];
    const dragItem = newData[dragIndex];

    // Remove the dragged item
    newData.splice(dragIndex, 1);
    // Insert at the new position
    newData.splice(hoverIndex, 0, dragItem);

    setItemsPagintion({
      ...itemsPagination,
      items: newData,
    });
  };

  const columns: ColumnsType<ISize> = [
    {
      title: 'Size Name',
      key: 'size',
      render: (_, record) => (
        <Link to={`/size/${record.id}/edit`}>
          <p>{record.size}</p>
        </Link>
      ),
    },
    {
      title: 'Product',
      key: 'product',
      dataIndex: 'product',
    },
    {
      title: 'Price',
      key: 'price',
      dataIndex: 'price',
    },
    {
      title: 'Description',
      key: 'description',
      dataIndex: 'description',
    },
  ];

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const page = parseInt(params.get('page') || '', 10) || 1;
    const perPage = parseInt(params.get('perPage') || '', 10) || PER_PAGE;
    setCanReorder(!!product);
    loadData(page, perPage);
  }, [location]);

  const loadData = async (
    page = itemsPagination?.page,
    perPage = itemsPagination?.perPage
  ) => {
    try {
      const res = await orderApi.getSizes({
        page,
        perPage,
        product,
      });

      setItemsPagintion(res);
    } catch (error) {
      setItemsPagintion({
        items: [],
        page,
        perPage,
        total: 0,
        pageCount: 0,
      });
      console.error(error);
    }
  };

  const onStateChanges = async (
    page: number,
    perPage: number,
    product?: ProductType
  ) => {
    const params = new URLSearchParams();
    params.set('page', String(page));
    params.set('perPage', String(perPage));
    // Only include the product filter when one is actually selected so we
    // don't send `product=undefined` or `product=` (which the backend treats
    // as a real filter and returns zero rows).
    if (product) {
      params.set('product', product);
    }
    navigate(`${location.pathname}?${params.toString()}`, { replace: true });
  };

  const onPageChange = (page: number, pageSize: number) => {
    onStateChanges(page, pageSize, product);
  };

  // Save the reordered items to backend
  const saveReorderedItems = async () => {
    try {
      await orderApi.updateSizeOrder(
        itemsPagination.items.map((item, index) => ({
          ...item,
          numericalOrder: index + 1,
        }))
      );
      message.success('Size order saved successfully');
    } catch (error) {
      console.error('Failed to save new order:', error);
      message.error('Failed to save size order');
    }
  };

  // Table components for drag and drop
  const components = {
    body: {
      row: DraggableBodyRow,
    },
  };

  // Need to use this workaround to fix TS errors
  // @ts-ignore
  const processedProps = {
    className: 'm__t--20',
    columns: columns,
    dataSource: itemsPagination.items,
    pagination: {
      onChange: onPageChange,
      total: itemsPagination.total,
      pageSize: itemsPagination.perPage,
    },
    components: canReorder ? components : undefined,
    onRow: (record: any, index: any) => ({
      record,
      index,
      moveRow,
      canDrag: canReorder,
    }),
    rowKey: 'id',
  };

  return (
    <DndProvider backend={HTML5Backend}>
      <PageContainer>
        <PageHeader
          title="Manage Size"
          addButton={
            <FlexContainer>
              {canReorder && (
                <Button onClick={saveReorderedItems} className="m__r--10">
                  Save Order
                </Button>
              )}

              <StyledSelect
                className="m__r--10"
                style={{ width: 150 }}
                placeholder="Choose Product"
                value={product ?? ''}
                onChange={(val: any) => {
                  // "View All" has value '' — coerce to undefined so the
                  // filter is dropped from the URL and the API call.
                  onStateChanges(1, PER_PAGE, val || undefined);
                }}
                options={ProductTypeOptions}
              />

              <Link to="/size/create">
                <Button className="w-100 font-weight--500" key="1">
                  + Create Size
                </Button>
              </Link>
            </FlexContainer>
          }
        />

        {/* @ts-ignore */}
        <Table {...processedProps} />
      </PageContainer>
    </DndProvider>
  );
};
export default SizeList;
